export const useSelectedDayStore = defineStore('selectedDay', () => {

  // Estado reactivo
  const selectedDay = ref<Date | null>(null)
  const selectedDestStore = useDestinationStore()
  const { selectedDestination } = storeToRefs(selectedDestStore)
  const date = computed(() => selectedDay.value ? formatDateApi(selectedDay.value) : formatDateApi(new Date(new Date().setHours(12, 0, 0, 0))))
  var selectedDiaCookie: Ref<Date> = useCookie('selectedDia', { expires: new Date(new Date().setHours(23, 59, 59, 999)) }) // cookie fecha, expira al final del día

  const horaCambio = computed(() => {
    const latamCountriesHour = ['AR', 'BR', 'CL', 'MX', 'CO', 'PE', 'EC', 'PA']
    if (latamCountriesHour.includes(selectedDestination.value?.country_id ?? '')) {
      return 18
    }
    return selectedDestination.value?.country_id === 'ES' ? 15 : 12
  })

  // Watcher para re-evaluar la fecha cuando el destino cambia (útil para el primer load con GeoIP)
  watch(selectedDestination, (newDest, oldDest) => {
    const route = useRoute()
    // Si el destino se acaba de inicializar (p.ej. por GeoIP) y no tenemos una fecha explícita en la URL ni en la cookie,
    // re-evaluamos para aplicar la horaCambio correcta de ese país.
    if (newDest && !oldDest && !route.query['data-checkinDate'] && !selectedDiaCookie.value) {
      init()
    }
  })

  // Funciones
  const init = () => {
    const now = new Date()
    const currentHour = now.getHours()

    // Default today/tomorrow relative to environment
    const todayLocal = new Date(new Date(now).setHours(12, 0, 0, 0))
    const tomorrowLocal = new Date(todayLocal)
    tomorrowLocal.setDate(todayLocal.getDate() + 1)

    const route = useRoute()

    // 1. Prioridad: Fecha en la URL (p.ej. navegación a search)
    if (route.query['data-checkinDate']) {
      selectedDay.value = getCookieDate(String(route.query['data-checkinDate']))
      return
    }

    // 2. Cookie: Fecha guardada previamente
    if (selectedDiaCookie.value) {
      const cookieDate = new Date(selectedDiaCookie.value)
      const nowForCheck = new Date()
      // Normalizamos a las 12:00 del día actual para comparar
      const todayThreshold = new Date(nowForCheck.getFullYear(), nowForCheck.getMonth(), nowForCheck.getDate(), 12, 0, 0)

      if (cookieDate < todayThreshold) {
        // Si la fecha guardada es anterior a hoy → reescribimos con hoy
        const utcToday = new Date(Date.UTC(nowForCheck.getFullYear(), nowForCheck.getMonth(), nowForCheck.getDate(), 12, 0, 0))
        selectedDiaCookie.value = utcToday
        selectedDay.value = todayLocal
      } else {
        selectedDay.value = cookieDate
      }
      return
    }

    // 3. Default: Si no hay nada, calculamos según la hora
    if (currentHour >= horaCambio.value) {
      selectedDay.value = tomorrowLocal
    } else {
      selectedDay.value = todayLocal
    }
  }


  const setDay = (date: Date) => {
    // Normalizamos a las 12:00 local para evitar problemas de zona horaria / SSR
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0)

    // Si la fecha es la misma que ya tenemos, no hacemos nada (evita bucles infinitos)
    if (selectedDay.value && selectedDay.value.getTime() === normalizedDate.getTime()) {
      return
    }

    selectedDay.value = normalizedDate

    // Extraemos año, mes, día en local
    const year = normalizedDate.getFullYear()
    const month = normalizedDate.getMonth()  // 0-indexed
    const day = normalizedDate.getDate()

    // Creamos una fecha UTC segura a las 12:00
    const utcSafeDate = new Date(Date.UTC(year, month, day, 12, 0, 0))
    selectedDiaCookie.value = utcSafeDate
  }

  const setNextDay = () => {
    const nextDay = selectedDay.value ? new Date(selectedDay.value) : new Date()
    nextDay.setDate(nextDay.getDate() + 1)
    setDay(nextDay)
  }

  const setPrevDay = () => {
    const prevDay = selectedDay.value ? new Date(selectedDay.value) : new Date()
    prevDay.setDate(prevDay.getDate() - 1)
    setDay(prevDay)
  }

  const setToday = () => {
    setDay(new Date(new Date().setHours(12, 0, 0, 0)))
  }

  const setTomorrow = () => {
    const tomorrow = new Date(new Date().setHours(12, 0, 0, 0))
    tomorrow.setDate(tomorrow.getDate() + 1)
    setDay(tomorrow)
  }

  const isToday = () => {
    const today = new Date(new Date().setHours(12, 0, 0, 0))
    const currentDate = selectedDay.value ? new Date(selectedDay.value) : new Date()
    return currentDate.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)
  }

  const isPrevToday = (date: Date) => {
    const today = new Date(new Date().setHours(12, 0, 0, 0))
    return date.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0)
  }


  return {
    selectedDay,
    date,
    init,
    setDay,
    setNextDay,
    setPrevDay,
    setToday,
    setTomorrow,
    isToday
  }
})